import React, { useState } from 'react';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import { Partner, PartnerUpdate } from '@marageti/z4-sdk/lib/travel/Partner';
import { Phone } from '@marageti/z4-sdk/lib/standards';
import { useParams } from 'react-router-dom';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import { phoneToE164 } from '../../../../../utils/helpers/validation';
import { ContainerProps } from './types';
import EditUserFormView from './edit-user-form-view';

const EditPartnerFormContainer = ({
  open,
  onClose,
}: ContainerProps) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    mode: 'onChange',
  });

  const { partnerId } = useParams();
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const [submissionError, setSubmissionError] = useState<ApiError>();
  const { data: partner, mutate: mutatePartner } = useFetch<Partner>(apiClient.partnersClient.getPartner, [partnerId], !!partnerId);

  const handleCancel = () => {
    reset();
    onClose();
  };

  const submitHandler: SubmitHandler<FieldValues> = async (data) => {
    if (!partner) return;

    const { smsPhone, ...partnerFields } = data;
    const updatedPartner: PartnerUpdate = {
      ...partner,
      ...partnerFields,
      smsPhoneNumber: phoneToE164(smsPhone as Phone | undefined),
    };

    try {
      await apiClient.partnersClient.putPartner(partner.id, updatedPartner);
      mutatePartner();
      enqueueSnackbar('Profile details updated.', { variant: 'success' });
      onClose();
    } catch (error) {
      setSubmissionError(error as ApiError);
      const errorMessage = (error as ApiError)?.message || 'An error occurred during the update.';
      enqueueSnackbar(
        errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1),
        { variant: 'default' },
      );
    }
  };

  return (
    <EditUserFormView
      control={control}
      errors={errors}
      handleSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      onCancel={handleCancel}
      submitHandler={submitHandler}
      open={open}
      register={register}
      submissionError={submissionError}
      partner={partner}
    />
  );
};

export default EditPartnerFormContainer;
